import { Auth0ContextInterface } from '@auth0/auth0-react'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

export interface Pool {
  poolId: string
  name: string
  description: string
  type: 'football' | 'horse-racing' | 'cornhole'
  status: 'active' | 'completed' | 'cancelled'
  startDate: string
  endDate?: string
  maxParticipants: number
  currentParticipants: number
  entryFee: number
  pickDeadline?: string
  settings: Record<string, any>
  createdBy: string
  createdAt: string
  updatedAt: string
  participants?: PoolParticipant[]
  games?: FootballGame[]
}

export interface PoolParticipant {
  poolId: string
  userId: string
  joinedAt: string
}

export interface FootballGame {
  poolId: string
  gameId: string
  week: number
  season: number
  homeTeam: string
  awayTeam: string
  gameTime: string
  spread: number
  overUnder?: number
  status: 'scheduled' | 'in_progress' | 'final'
  homeScore?: number
  awayScore?: number
  winner?: 'home' | 'away'
  createdAt: string
  updatedAt: string
}

export interface CreatePoolData {
  name: string
  description?: string
  type: 'football' | 'horse-racing' | 'cornhole'
  startDate?: string
  endDate?: string
  maxParticipants?: number
  entryFee?: number
  settings?: Record<string, any>
}

export interface UpdatePoolData {
  name?: string
  description?: string
  status?: 'active' | 'completed' | 'cancelled'
  endDate?: string
  maxParticipants?: number
  settings?: Record<string, any>
}

export interface CollegeFootballGame {
  id: number
  season: number
  week: number
  seasonType: string
  startDate: string
  startTimeTBD: boolean
  completed: boolean
  neutralSite: boolean
  conferenceGame: boolean
  attendance?: number
  venue?: string
  venueId?: number
  homeTeam: {
    id: number
    team: string
    classification: string
    conference: string
    points?: number
    lineScores?: number[]
    postgameWinProbability?: number
  }
  awayTeam: {
    id: number
    team: string
    classification: string
    conference: string
    points?: number
    lineScores?: number[]
    postgameWinProbability?: number
  }
  excitementIndex?: number
  highlights?: string
  notes?: string
}

export interface BettingLine {
  id: number
  season: number
  week: number
  seasonType: string
  startDate: string
  homeTeam: {
    id: number
    team: string
    conference: string
    score?: number
  }
  awayTeam: {
    id: number
    team: string
    conference: string
    score?: number
  }
  lines: {
    provider: string
    spread: number
    formattedSpread: string
    spreadOpen?: number
    overUnder: number
    overUnderOpen?: number
    homeMoneyline?: number
    awayMoneyline?: number
  }[]
}

export interface MediaInfo {
  id: number
  season: number
  week: number
  seasonType: string
  startTime: string
  isStartTimeTBD: boolean
  homeTeam: string
  homeConference: string
  awayTeam: string
  awayConference: string
  mediaType: 'tv' | 'web'
  outlet: string
}

class PoolsApi {
  private auth0: Auth0ContextInterface

  constructor(auth0: Auth0ContextInterface) {
    this.auth0 = auth0
  }

  private async getHeaders(): Promise<HeadersInit> {
    try {
      const token = await this.auth0.getAccessTokenSilently()
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    } catch (error) {
      console.error('Error getting access token:', error)
      throw new Error('Authentication failed')
    }
  }

  // Pool Management
  async getPools(): Promise<{ pools: Pool[], total: number }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools`, {
      headers
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch pools: ${response.statusText}`)
    }

    return response.json()
  }

  async getPool(poolId: string): Promise<{ pool: Pool }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
      headers
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch pool: ${response.statusText}`)
    }

    return response.json()
  }

  async createPool(data: CreatePoolData): Promise<{ pool: Pool }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to create pool: ${response.statusText}`)
    }

    return response.json()
  }

  async updatePool(poolId: string, data: UpdatePoolData): Promise<{ pool: Pool }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to update pool: ${response.statusText}`)
    }

    return response.json()
  }

  async joinPool(poolId: string): Promise<{ participant: PoolParticipant }> {
    const headers = await this.getHeaders()
    const url = `${API_BASE_URL}/pools/${poolId}/participants`
    
    console.log('Joining pool with URL:', url)
    console.log('Headers:', headers)
    
    const response = await fetch(url, {
      method: 'POST',
      headers
    })

    console.log('Join pool response status:', response.status)
    console.log('Join pool response headers:', Object.fromEntries(response.headers.entries()))

    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      console.log('Error response content-type:', contentType)
      
      let errorMessage = `Failed to join pool: ${response.status} ${response.statusText}`
      
      try {
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } else {
          // If it's not JSON, get the text (likely HTML error page)
          const errorText = await response.text()
          console.log('Non-JSON error response:', errorText.substring(0, 500))
          errorMessage = `Server returned HTML instead of JSON. Status: ${response.status}`
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError)
      }
      
      throw new Error(errorMessage)
    }

    return response.json()
  }

  async leavePool(poolId: string, userId: string): Promise<{ message: string }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/participants/${userId}`, {
      method: 'DELETE',
      headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to leave pool: ${response.statusText}`)
    }

    return response.json()
  }

  // College Football Data API
  async getCollegeFootballGames(params: {
    year?: number
    week?: number
    seasonType?: string
    gameDate?: string
    startDate?: string
    endDate?: string
    team?: string
    conference?: string
  }): Promise<{ games: CollegeFootballGame[], total: number }> {
    const headers = await this.getHeaders()
    const queryParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString())
      }
    })

    const response = await fetch(`${API_BASE_URL}/college-football-data/games?${queryParams}`, {
      headers
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch college football games: ${response.statusText}`)
    }

    return response.json()
  }

  async getBettingLines(params: {
    year?: number
    week?: number
    seasonType?: string
    gameDate?: string
    startDate?: string
    endDate?: string
    team?: string
    gameId?: number
  }): Promise<{ lines: BettingLine[], total: number }> {
    const headers = await this.getHeaders()
    const queryParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString())
      }
    })

    const response = await fetch(`${API_BASE_URL}/college-football-data/lines?${queryParams}`, {
      headers
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch betting lines: ${response.statusText}`)
    }

    return response.json()
  }

  async getMediaInfo(params: {
    year?: number
    week?: number
    seasonType?: string
    gameDate?: string
    startDate?: string
    endDate?: string
    team?: string
    mediaType?: 'tv' | 'web'
  }): Promise<{ media: MediaInfo[], total: number }> {
    const headers = await this.getHeaders()
    const queryParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString())
      }
    })

    const response = await fetch(`${API_BASE_URL}/college-football-data/media?${queryParams}`, {
      headers
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch media info: ${response.statusText}`)
    }

    return response.json()
  }

  async getCombinedGames(params: {
    year?: number
    week?: number
    seasonType?: string
    gameDate?: string
    startDate?: string
    endDate?: string
    team?: string
    gameId?: number
  }): Promise<{ games: any[], total: number }> {
    const headers = await this.getHeaders()
    const queryParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString())
      }
    })

    const response = await fetch(`${API_BASE_URL}/college-football-data/combined-games?${queryParams}`, {
      headers
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch combined games: ${response.statusText}`)
    }

    return response.json()
  }

  // Football Pool Specific
  async addGamesToPool(poolId: string, data: {
    games: {
      homeTeam: string
      awayTeam: string
      gameTime: string
      spread?: number
      overUnder?: number
    }[]
    week: number
    season: number
  }): Promise<{ games: FootballGame[] }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/games`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to add games to pool: ${response.statusText}`)
    }

    return response.json()
  }

  async setGameResult(poolId: string, gameId: string, data: {
    homeScore: number
    awayScore: number
    winner?: 'home' | 'away'
  }): Promise<{ game: FootballGame }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/games/${gameId}/result`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to set game result: ${response.statusText}`)
    }

    return response.json()
  }

  async updateScoresFromAPI(poolId: string): Promise<{ 
    message: string
    totalGamesChecked: number
    updatedGames: Array<{
      gameId: string
      homeTeam: string
      awayTeam: string
      homeScore: number | null
      awayScore: number | null
      completed: boolean
      status: string
      winner: string | null
    }>
  }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/update-scores`, {
      method: 'POST',
      headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to update scores: ${response.statusText}`)
    }

    return response.json()
  }

  async deletePool(poolId: string): Promise<{ message: string, deletedItems: number }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
      method: 'DELETE',
      headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to delete pool: ${response.statusText}`)
    }

    return response.json()
  }

  async markParticipantPaid(poolId: string, participantUserId: string, paid: boolean): Promise<{ message: string, participant: any }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/participants/${participantUserId}/payment`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ paid })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to update payment status: ${response.statusText}`)
    }

    return response.json()
  }

  // Get user's own picks for a pool
  async getUserPicks(poolId: string): Promise<{ picks: any[] }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/picks`, {
      method: 'GET',
      headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to get picks: ${response.statusText}`)
    }

    return response.json()
  }

  // Save user's picks for a pool
  async saveUserPicks(poolId: string, picks: any[]): Promise<{ message: string }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/picks`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ picks })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to save picks: ${response.statusText}`)
    }

    return response.json()
  }

  // Get all picks for a pool (Pool Admin only)
  async getAllPoolPicks(poolId: string): Promise<{ picks: any[] }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/picks/all`, {
      method: 'GET',
      headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to get all picks: ${response.statusText}`)
    }

    return response.json()
  }

  // Get pool standings
  async getPoolStandings(poolId: string): Promise<any> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/pools/${poolId}/standings`, {
      method: 'GET',
      headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || `Failed to get standings: ${response.statusText}`)
    }

    return response.json()
  }

}

// Note: PoolsApi requires Auth0 context, so it should be instantiated in components
// Example: const poolsApi = new PoolsApi(auth0)
export default PoolsApi

// For convenience, you can also create an instance if you have access to auth0 context
// export const poolsApi = new PoolsApi(auth0)